import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip
} from 'recharts';
import { translateCampaignStatus } from '../../../utils/statusTranslations';

const CampaignStats = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
        Nenhuma campanha encontrada
      </div>
    );
  }

  // Calcular estatísticas agregadas
  const totalSent = data.reduce((sum, campaign) => sum + campaign.sent_count, 0);
  const totalResponded = data.reduce((sum, campaign) => sum + campaign.response_count, 0);
  const totalFailed = data.reduce((sum, campaign) => sum + campaign.failed_count, 0);

  // Dados para o gráfico de pizza
  const pieData = [
    { name: 'Respondidas', value: totalResponded, color: '#10b981' },
    { name: 'Enviadas', value: totalSent - totalResponded - totalFailed, color: '#3b82f6' },
    { name: 'Falharam', value: totalFailed, color: '#ef4444' }
  ].filter(item => item.value > 0);

  // Estatísticas por status de campanha
  const statusStats = data.reduce((acc, campaign) => {
    acc[campaign.status] = (acc[campaign.status] || 0) + 1;
    return acc;
  }, {});

  const statusData = Object.entries(statusStats).map(([status, count]) => ({
    status,
    count,
    color: status === 'completed' ? '#10b981' : 
           status === 'active' ? '#3b82f6' : 
           status === 'failed' ? '#ef4444' : '#6b7280'
  }));

  return (
    <div className="space-y-6">
      {/* Estatísticas gerais */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{totalSent}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Total Enviadas</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">{totalResponded}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Respondidas</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-red-600">{totalFailed}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Falharam</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico de distribuição de mensagens */}
        <div>
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Distribuição de Mensagens
          </h4>
          {pieData.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
              Nenhum dado para exibir
            </div>
          )}
        </div>

        {/* Status das campanhas */}
        <div>
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Status das Campanhas
          </h4>
          <div className="space-y-3">
            {statusData.map((item, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div 
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: item.color }}
                  ></div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {translateCampaignStatus(item.status)}
                  </span>
                </div>
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  {item.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top campanhas por taxa de resposta */}
      <div>
        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Top Campanhas por Taxa de Resposta
        </h4>
        <div className="space-y-2">
          {data
            .filter(campaign => campaign.sent_count > 0)
            .sort((a, b) => b.response_rate - a.response_rate)
            .slice(0, 5)
            .map((campaign, index) => (
              <div key={campaign.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {campaign.name}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    {campaign.sent_count} enviadas • {campaign.response_count} respostas
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-20 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full" 
                      style={{ width: `${Math.min(campaign.response_rate, 100)}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-green-600 w-12">
                    {campaign.response_rate}%
                  </span>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default CampaignStats;